import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import ImageUpload from "@/components/admin/ImageUpload";

const fieldCls =
  "w-full border border-white/15 bg-black/40 px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#D4AF37]";

export default function ItemManager({ collection, title, subtitle, fields, testidPrefix, cardTitle, cardSubtitle }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/admin/items/${collection}`)
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [collection]);

  useEffect(() => load(), [load]);

  const defaults = () =>
    fields.reduce((acc, f) => ({ ...acc, [f.name]: f.type === "switch" ? f.default ?? true : "" }), {});

  const openNew = () => {
    setEditing(null);
    setForm(defaults());
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm(fields.reduce((acc, f) => ({ ...acc, [f.name]: item[f.name] ?? (f.type === "switch" ? true : "") }), {}));
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/items/${collection}/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post(`/admin/items/${collection}`, form);
        toast.success("Created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${cardTitle(item)}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/items/${collection}/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const imageField = fields.find((f) => f.type === "image");

  return (
    <div data-testid={`${testidPrefix}-manager`}>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-medium text-white">{title}</h1>
          <p className="mt-2 text-sm font-light text-neutral-400">{subtitle}</p>
        </div>
        <button
          data-testid={`${testidPrefix}-add-button`}
          onClick={openNew}
          className="flex items-center gap-2 bg-[#D4AF37] px-6 py-3 text-xs font-medium uppercase tracking-[0.25em] text-black transition-colors hover:bg-[#F9F1CC]"
        >
          <Plus className="h-4 w-4" strokeWidth={1.5} /> Add New
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-sm text-neutral-500">Loading...</div>
      ) : items.length === 0 ? (
        <div data-testid={`${testidPrefix}-empty`} className="border border-dashed border-white/15 py-20 text-center text-sm text-neutral-500">
          Nothing here yet. Click "Add New" to begin.
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((item, i) => (
            <motion.div
              key={item.id}
              data-testid={`${testidPrefix}-item-${i}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04, duration: 0.4 }}
              className="group border border-white/10 bg-[#0C0C0C] transition-colors hover:border-[#D4AF37]/40"
            >
              {imageField && item[imageField.name] && (
                <img src={item[imageField.name]} alt="" className="h-44 w-full bg-black object-contain" />
              )}
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display truncate text-xl text-white">{cardTitle(item)}</div>
                    {cardSubtitle && <div className="mt-1 line-clamp-2 text-xs font-light text-neutral-400">{cardSubtitle(item)}</div>}
                    {item.published !== undefined && (
                      <span data-testid={`${testidPrefix}-published-${i}`} className={`mt-3 inline-block px-2 py-1 text-[9px] uppercase tracking-[0.2em] ${item.published ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "bg-white/10 text-neutral-400"}`}>
                        {item.published ? "Published" : "Draft"}
                      </span>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button data-testid={`${testidPrefix}-edit-${i}`} onClick={() => openEdit(item)} className="border border-white/15 p-2 text-neutral-300 transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]" aria-label="Edit">
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                    <button data-testid={`${testidPrefix}-delete-${i}`} onClick={() => remove(item)} className="border border-white/15 p-2 text-neutral-300 transition-colors hover:border-red-500 hover:text-red-400" aria-label="Delete">
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
            data-testid={`${testidPrefix}-dialog`}
          >
            <motion.div
              initial={{ y: 32, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[90vh] w-full max-w-xl overflow-y-auto border border-[#D4AF37]/25 bg-[#0C0C0C] p-8"
            >
              <div className="mb-6 flex items-center justify-between">
                <h2 className="font-display text-2xl text-white">{editing ? "Edit" : "Add"} {title.replace(/s$/, "")}</h2>
                <button data-testid={`${testidPrefix}-dialog-close`} onClick={() => setOpen(false)} className="text-neutral-400 hover:text-[#D4AF37]" aria-label="Close">
                  <X className="h-5 w-5" strokeWidth={1.5} />
                </button>
              </div>
              <form onSubmit={save} className="space-y-5">
                {fields.map((f) => (
                  <div key={f.name}>
                    <label className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-neutral-400">{f.label}</label>
                    {f.type === "textarea" ? (
                      <textarea
                        data-testid={`${testidPrefix}-field-${f.name}`}
                        rows={f.rows || 4}
                        required={f.required !== false}
                        value={form[f.name] ?? ""}
                        onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
                        className={`${fieldCls} resize-y`}
                      />
                    ) : f.type === "image" ? (
                      <ImageUpload
                        testid={`${testidPrefix}-field-${f.name}`}
                        value={form[f.name]}
                        onChange={(url) => setForm((s) => ({ ...s, [f.name]: url }))}
                      />
                    ) : f.type === "switch" ? (
                      <label className="flex cursor-pointer items-center gap-3 text-sm text-neutral-300">
                        <input
                          type="checkbox"
                          data-testid={`${testidPrefix}-field-${f.name}`}
                          checked={!!form[f.name]}
                          onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.checked }))}
                          className="h-4 w-4 accent-[#D4AF37]"
                        />
                        {f.hint || "Yes"}
                      </label>
                    ) : (
                      <input
                        data-testid={`${testidPrefix}-field-${f.name}`}
                        required={f.required !== false}
                        value={form[f.name] ?? ""}
                        onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
                        className={fieldCls}
                      />
                    )}
                  </div>
                ))}
                <button
                  data-testid={`${testidPrefix}-save-button`}
                  type="submit"
                  disabled={saving}
                  className="w-full bg-[#D4AF37] px-6 py-4 text-xs font-medium uppercase tracking-[0.3em] text-black transition-colors hover:bg-[#F9F1CC] disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
